import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@volca/ui/lib/utils"
import type { StatusColor } from "@volca/ui/tokens/component-colors"

type AlertColor = Exclude<StatusColor, "brand">

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg border px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      color: {
        neutral: "border-line bg-surface-raised text-ink",
        danger:
          "border-danger-line bg-danger-subtle text-danger *:data-[slot=alert-description]:text-danger",
        warning:
          "border-warning-line bg-warning-subtle text-warning *:data-[slot=alert-description]:text-warning",
        success:
          "border-success-line bg-success-subtle text-success *:data-[slot=alert-description]:text-success",
        information:
          "border-information-line bg-information-subtle text-information *:data-[slot=alert-description]:text-information",
      } satisfies Record<AlertColor, string>,
    },
    defaultVariants: {
      color: "neutral",
    },
  }
)

function Alert({
  className,
  color,
  ...props
}: Omit<React.ComponentProps<"div">, "color"> & { color?: AlertColor }) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ color }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "leading-[1.3] font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-ink",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-sm leading-[1.5] text-balance text-subtle md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-ink [&_p:not(:last-child)]:mb-4",
        className
      )}
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-action"
      className={cn("absolute top-2 right-2", className)}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription, AlertAction, type AlertColor }
